/* eslint-disable max-lines */
import { Link } from '@tanstack/react-router';
import { Calendar, Clock } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import type { Incident, IncidentSource } from '@/lib/queries/rca/incidents';

import { getPrimaryIncidentData, getSourceIcon } from './IncidentCardHelpers';
import { IncidentStatusBadge } from './IncidentStatus';
import { PriorityBadge } from './SeverityBadge';

interface IncidentCardProps {
  incident: Incident;
}

interface IncidentContentProps {
  title: string;
  summary?: string;
}

interface IncidentTimelineProps {
  status: string;
  startedAt: string;
  resolvedAt?: string;
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const IncidentSources = ({
  sources,
}: {
  sources: IncidentSource[];
}): JSX.Element => (
  <div className="flex items-center gap-2">
    {sources.map(source => (
      <div
        key={source}
        className="flex-shrink-0 w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center"
        title={source}
      >
        <img src={getSourceIcon(source)} alt={source} className="w-5 h-5" />
      </div>
    ))}
  </div>
);

const IncidentNumber = ({
  incidentNumber,
}: {
  incidentNumber?: number;
}): JSX.Element => (
  <span className="text-lg font-mono font-semibold text-gray-600 dark:text-gray-400">
    #{incidentNumber}
  </span>
);

// const IncidentBadges = ({
//   status,
//   priority,
//   severity,
// }: {
//   status: string;
//   priority?: string;
//   severity?: string;
// }): JSX.Element => (
//   <div className="flex items-center gap-2 flex-wrap min-h-[2rem]">
//     <IncidentStatusBadge
//       status={status as 'triggered' | 'acknowledged' | 'resolved'}
//     />
//     {priority !== undefined && priority !== '' && (
//       <PriorityBadge priority={priority} />
//     )}
//     {severity !== undefined && severity !== '' && (
//       <PriorityBadge priority={severity} />
//     )}
//   </div>
// );

const IncidentContent = ({
  title,
  summary,
}: IncidentContentProps): JSX.Element => (
  <div className="flex-1 min-w-0">
    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-1">
      {title}
    </h3>
    {summary !== undefined && (
      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
        {summary}
      </p>
    )}
  </div>
);

const IncidentTimeline = ({
  status,
  startedAt,
  resolvedAt,
}: IncidentTimelineProps): JSX.Element => {
  const isResolved = status === 'resolved';
  const startDate = startedAt;

  return (
    <div className="flex-shrink-0 lg:text-right space-y-1 w-full">
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 lg:justify-end">
        <Calendar className="w-3 h-3 flex-shrink-0" />
        <span>
          <FormattedMessage id="incidents.card.started" />:{' '}
          {formatDate(startDate)}
        </span>
      </div>

      {isResolved &&
        Boolean(resolvedAt) &&
        resolvedAt !== undefined &&
        resolvedAt.length > 0 && (
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 lg:justify-end">
            <Clock className="w-3 h-3 flex-shrink-0" />
            <span>
              <FormattedMessage id="incidents.card.resolved" />:{' '}
              {formatDate(resolvedAt!)}
            </span>
          </div>
        )}
    </div>
  );
};

export const IncidentCard = ({
  incident,
}: IncidentCardProps): JSX.Element | null => {
  const primaryData = getPrimaryIncidentData(incident);

  if (!primaryData) {
    return null;
  }

  const {
    incidentNumber,
    title,
    summary,
    priority,
    startedAt,
    resolvedAt,
    sourceStatus,
  } = primaryData;

  return (
    <Link
      to="/rca/$incidentId"
      params={{ incidentId: incident.id }}
      className="block"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer w-full">
        <div className="grid grid-cols-1 lg:grid-cols-[4rem_5rem_9rem_4rem_auto_auto] gap-2 lg:gap-4 lg:items-center">
          {/* Sources - fixed width for alignment */}
          <div className="flex items-center justify-center lg:justify-center">
            <IncidentSources sources={incident.sources} />
          </div>

          {/* Incident Number - fixed width for alignment */}
          <div className="flex items-center justify-center lg:justify-center">
            {incidentNumber !== undefined && (
              <IncidentNumber incidentNumber={incidentNumber} />
            )}
          </div>

          {/* Status Badge - fixed width to accommodate "Acknowledged" */}
          <div className="flex items-center justify-center lg:justify-center">
            <IncidentStatusBadge
              sourceStatus={
                sourceStatus as 'triggered' | 'acknowledged' | 'resolved'
              }
            />
          </div>

          {/* Severity Badge - fixed width for perfect vertical alignment */}
          <div className="flex items-center justify-center lg:justify-center">
            {priority !== undefined && priority !== '' && (
              <PriorityBadge priority={priority} />
            )}
          </div>

          {/* Title & Description - grows to fill remaining space */}
          <div className="flex items-center min-w-0 lg:px-2">
            <IncidentContent title={title} summary={summary} />
          </div>

          {/* Timeline - auto width, right aligned */}
          <div className="flex items-center justify-center lg:justify-end">
            <IncidentTimeline
              status={status}
              startedAt={startedAt}
              resolvedAt={resolvedAt}
            />
          </div>
        </div>
      </div>
    </Link>
  );
};
